import { useEffect, useRef } from "react";
import splashImg from "images/download-banner/splash.png";
import topoLinesImg from "images/download-banner/topo-lines.png";
import topGradientImg from "images/download-banner/top-gradient.png";
import linesLitSrc from "images/download-banner/lines-lit.png";
import { ReactComponent as DownloadIcon } from "images/download-banner/download.svg";

interface AppDownloadBannerProps {
  downloadHref?: string;
}

const CANVAS_W = 2032;
const CANVAS_H = 120;
const MAX_RIPPLES = 2;
const GAP: [number, number] = [3.0, 4.6];
const DURATION: [number, number] = [2.2, 3.0];
const MAX_RADIUS: [number, number] = [300, 400];
const SPAWN_X: [number, number] = [120, 1250];
const SPAWN_Y: [number, number] = [25, 95];
const LINE_STRENGTH = 0.62;
const RING_GLOW = 0.07;
const TAU = Math.PI * 2;

function rnd(a: number, b: number) {
  return a + Math.random() * (b - a);
}

interface Ripple {
  x: number;
  y: number;
  age: number;
  dur: number;
  R: number;
  rad: number;
  a: number;
}

export function AppDownloadBanner({ downloadHref = "/app?utm_source=UGG&utm_campaign=BuildPage" }: AppDownloadBannerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = canvas.getContext("2d")!;
    if (!ctx) return;

    const mask = document.createElement("canvas");
    mask.width = CANVAS_W;
    mask.height = CANVAS_H;
    const mg = mask.getContext("2d")!;

    const lit = new Image();
    lit.src = linesLitSrc;

    let rafId = 0;
    let lastNow = -1;
    let t = 0;
    let imageLoaded = false;
    let inView = false;

    const ripples: Ripple[] = [];
    let nextSpawn = 0;

    function frame(now: number) {
      const dt = lastNow < 0 ? 0 : Math.max(0, Math.min(0.05, (now - lastNow) / 1000));
      lastNow = now;
      t += dt;

      if (ripples.length < MAX_RIPPLES && t >= nextSpawn) {
        ripples.push({
          x: rnd(...SPAWN_X),
          y: rnd(...SPAWN_Y),
          age: 0,
          dur: rnd(...DURATION),
          R: rnd(...MAX_RADIUS),
          rad: 0,
          a: 1,
        });
        nextSpawn = t + rnd(...GAP);
      }

      mg.clearRect(0, 0, CANVAS_W, CANVAS_H);
      for (const r of ripples) {
        r.age += dt;
        const u = Math.min(1, r.age / r.dur);
        r.rad = (1 - Math.pow(1 - u, 3)) * r.R;
        r.a = Math.pow(1 - u, 1.3);
        const w = 26 + 30 * u;
        const g = mg.createRadialGradient(r.x, r.y, Math.max(0, r.rad - w), r.x, r.y, r.rad + w);
        g.addColorStop(0, "rgba(0,0,0,0)");
        g.addColorStop(0.5, `rgba(0,0,0,${r.a})`);
        g.addColorStop(1, "rgba(0,0,0,0)");
        mg.fillStyle = g;
        mg.fillRect(r.x - r.rad - w, r.y - r.rad - w, (r.rad + w) * 2, (r.rad + w) * 2);
      }

      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
      ctx.globalAlpha = LINE_STRENGTH;
      ctx.drawImage(lit, 0, 0);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "destination-in";
      ctx.drawImage(mask, 0, 0);
      ctx.globalCompositeOperation = "source-over";

      for (const r of ripples) {
        ctx.strokeStyle = `rgba(244,200,116,${r.a * RING_GLOW})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(r.x, r.y, Math.max(0, r.rad), 0, TAU);
        ctx.stroke();
      }

      for (let i = ripples.length - 1; i >= 0; i--) {
        if (ripples[i].age >= ripples[i].dur) ripples.splice(i, 1);
      }

      rafId = requestAnimationFrame(frame);
    }

    function tryStart() {
      if (imageLoaded && inView && rafId === 0) {
        lastNow = -1;
        rafId = requestAnimationFrame(frame);
      }
    }

    function tryStop() {
      if (rafId !== 0) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
    }

    const observer = new IntersectionObserver((entries) => {
      inView = entries[0].isIntersecting;
      inView ? tryStart() : tryStop();
    });
    observer.observe(canvas);

    function onLitLoaded() {
      imageLoaded = true;
      tryStart();
    }

    lit.complete ? onLitLoaded() : lit.addEventListener("load", onLitLoaded);

    return () => {
      tryStop();
      observer.disconnect();
      lit.removeEventListener("load", onLitLoaded);
    };
  }, []);

  return (
    <div className="hidden md:block relative w-full max-w-[1016px] aspect-[1016/60] mt-[18px] overflow-hidden rounded-[3px] bg-[#070720] isolate">
      <div
        className="absolute right-0 top-0 w-[66.44%] h-full bg-no-repeat [background-size:100%_100%] pointer-events-none z-[1]"
        style={{ backgroundImage: `url(${splashImg})` }}
      />
      <div
        className="absolute left-0 top-0 w-full h-[98.33%] bg-no-repeat [background-size:100%_100%] pointer-events-none z-[2]"
        style={{ backgroundImage: `url(${topoLinesImg})` }}
      />
      <canvas
        ref={canvasRef}
        width={CANVAS_W}
        height={CANVAS_H}
        className="absolute inset-0 w-full h-full z-[3] pointer-events-none mix-blend-screen [-webkit-mask-image:linear-gradient(90deg,#000_0%,#000_46%,transparent_68%)] [mask-image:linear-gradient(90deg,#000_0%,#000_46%,transparent_68%)]"
      />
      <div
        className="absolute left-0 top-0 w-full h-full bg-no-repeat [background-size:100%_100%] pointer-events-none z-[4]"
        style={{ backgroundImage: `url(${topGradientImg})` }}
      />
      <div className="absolute left-[1.97%] top-1/2 -translate-y-1/2 z-[6] font-['Inter'] font-bold text-[16px] leading-[1.2] text-white whitespace-nowrap pointer-events-none">
        Auto-import the best builds, get live stat tracking, and scout your enemies.
      </div>
      <a
        href={downloadHref}
        className="absolute right-[1.18%] top-1/2 -translate-y-1/2 z-[6] flex items-center justify-center gap-[6px] px-[12px] w-[14.17%] h-[56.67%] bg-[#FF4655] hover:bg-[#ff3a3d] rounded-[3px] no-underline transition-all duration-150 [filter:drop-shadow(0_4px_8px_rgba(0,0,0,0.4))]"
      >
        <DownloadIcon className="shrink-0 w-5 h-5" />
        <span className="font-['Inter'] font-semibold text-[13px] text-white whitespace-nowrap">Download App</span>
      </a>
    </div>
  );
}
